import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { IContaBancaria, IPlanoContaOpcao, ISetupCategoria } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';

export type ModoCategoriaSetup =
    | { tipo: 'nova' }
    | { tipo: 'versao'; categoria: ISetupCategoria }
    | { tipo: 'excecao'; categoria: ISetupCategoria; contaBancariaId?: number };

interface Props {
    esteiraId: number;
    modo: ModoCategoriaSetup;
    plano: IPlanoContaOpcao[];
    contas: IContaBancaria[];
    onHide: () => void;
    onSalvo: () => void;
}

const descreverConta = (c: IContaBancaria) =>
    `Banco ${c.banco}${c.agencia ? ` · Ag. ${c.agencia}` : ''} · Conta ${c.conta}${c.descricao ? ` (${c.descricao})` : ''}`;

// DEVN3-768 — criar categoria, gravar nova versão da regra da empresa ou a exceção de uma conta
// bancária. Salvar já aprova: a versão anterior fica no histórico.
export default function ModalCategoriaSetup({ esteiraId, modo, plano, contas, onHide, onSalvo }: Props) {
    const categoria = modo.tipo === 'nova' ? null : modo.categoria;
    const excecaoAtual = modo.tipo === 'excecao' && modo.contaBancariaId
        ? modo.categoria.excecoes.find(e => e.contaBancariaId === modo.contaBancariaId) ?? null
        : null;

    const [nome, setNome] = useState('');
    const [direcao, setDirecao] = useState<'ENTRADA' | 'SAIDA'>('SAIDA');
    const [contaContabil, setContaContabil] = useState('');
    const [padroes, setPadroes] = useState('');
    const [vigencia, setVigencia] = useState('');
    const [contaBancariaId, setContaBancariaId] = useState(0);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setNome(categoria?.nome ?? '');
        setDirecao(categoria?.direcao ?? 'SAIDA');
        setContaContabil((modo.tipo === 'excecao' ? excecaoAtual?.contaContabil : categoria?.atual?.contaContabil) ?? '');
        setPadroes((categoria?.atual?.padroes ?? []).join('\n'));
        setVigencia('');
        setContaBancariaId(modo.tipo === 'excecao' ? modo.contaBancariaId ?? 0 : 0);
    }, [modo, categoria, excecaoAtual]);

    const opcoes = useMemo(() => plano.map(p => ({ value: p.codigo, label: `${p.codigo} — ${p.descricao}` })), [plano]);
    const selecionada = opcoes.find(o => o.value === contaContabil)
        // Conta que saiu do plano: aparece como está, para o usuário ver o que precisa trocar.
        ?? (contaContabil ? { value: contaContabil, label: `${contaContabil} — fora do plano deste período` } : null);

    const versaoAnterior = modo.tipo === 'excecao' ? excecaoAtual : categoria?.atual;
    // DEVN3-774: juros, multa, desconto e tarifa da cobrança não reconhecem linha do extrato: só a conta importa.
    const componente = ['JUROS_COBRANCA', 'MULTA_COBRANCA', 'DESCONTO_COBRANCA', 'TARIFA_COBRANCA', 'JUROS_TRIBUTO', 'MULTA_TRIBUTO',
        'IRRF_RECOLHER', 'ISS_RECOLHER', 'INSS_RECOLHER', 'CSRF_RECOLHER', 'IRRF_RECUPERAR', 'ISS_RECUPERAR', 'INSS_RECUPERAR', 'CSRF_RECUPERAR',
        'FOLHA_MENSAL', 'FOLHA_ADIANTAMENTO', 'FOLHA_DECIMO_TERCEIRO', 'FOLHA_FERIAS', 'FOLHA_RESCISAO', 'FOLHA_OUTROS'].includes(categoria?.codigoPadrao ?? '');

    const salvar = async () => {
        const listaPadroes = padroes.split('\n').map(p => p.trim()).filter(p => p);
        const vigenciaInicio = vigencia || null;
        if (modo.tipo === 'excecao' && !contaBancariaId) {
            Swal.fire({ text: 'Escolha a conta bancária.', icon: 'warning' });
            return;
        }
        let renomeou = false;
        setSalvando(true);
        try {
            if (modo.tipo === 'nova') {
                await ModuloBancarioService.criarCategoria(esteiraId, { nome, direcao, contaContabil, padroes: listaPadroes, vigenciaInicio });
            } else if (modo.tipo === 'versao') {
                if (nome.trim() !== modo.categoria.nome) {
                    await ModuloBancarioService.renomearCategoria(esteiraId, modo.categoria.id, nome);
                    renomeou = true;
                }
                await ModuloBancarioService.salvarVersao(esteiraId, modo.categoria.id, { contaContabil, padroes: listaPadroes, vigenciaInicio });
            } else {
                await ModuloBancarioService.salvarExcecao(esteiraId, modo.categoria.id, { contaBancariaId, contaContabil, vigenciaInicio });
            }
            Swal.fire({ text: 'Setup salvo e aprovado.', icon: 'success', timer: 2000, showConfirmButton: false });
            onSalvo();
            onHide();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? err?.response?.data?.message ?? 'Não foi possível salvar.', icon: 'error' });
            // O nome já foi gravado antes de a versão falhar.
            if (renomeou) onSalvo();
        } finally {
            setSalvando(false);
        }
    };

    const titulo = modo.tipo === 'nova' ? 'Nova categoria'
        : modo.tipo === 'versao' ? `Editar: ${modo.categoria.nome}`
        : `Exceção por conta bancária: ${modo.categoria.nome}`;

    return (
        <Modal show onHide={onHide} centered size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{titulo}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='row g-3'>
                    {modo.tipo !== 'excecao' && (
                        <div className='col-8'>
                            <label className='form-label required'>Nome</label>
                            <input className='form-control' value={nome} maxLength={100}
                                onChange={e => setNome(e.target.value)} />
                        </div>
                    )}
                    {modo.tipo !== 'excecao' && (
                        <div className='col-4'>
                            <label className='form-label required'>Direção no extrato</label>
                            <select className='form-select' value={direcao} disabled={modo.tipo !== 'nova'}
                                onChange={e => setDirecao(e.target.value as 'ENTRADA' | 'SAIDA')}>
                                <option value='SAIDA'>Saída</option>
                                <option value='ENTRADA'>Entrada</option>
                            </select>
                        </div>
                    )}
                    {modo.tipo === 'excecao' && (
                        <div className='col-12'>
                            <label className='form-label required'>Conta bancária</label>
                            <select className='form-select' value={contaBancariaId} disabled={!!modo.contaBancariaId}
                                onChange={e => setContaBancariaId(Number(e.target.value))}>
                                <option value={0}>Selecione</option>
                                {contas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                            </select>
                            <div className='form-text'>
                                Para esta conta bancária, a contrapartida da categoria passa a ser a conta abaixo. Os padrões de histórico são os da regra da empresa.
                            </div>
                        </div>
                    )}
                    <div className='col-12'>
                        <label className='form-label required'>Conta de contrapartida</label>
                        <Select
                            options={opcoes}
                            value={selecionada}
                            onChange={o => setContaContabil(o?.value ?? '')}
                            placeholder='Busque pelo código ou pela descrição'
                            noOptionsMessage={() => 'Nenhuma conta analítica encontrada'}
                        />
                        <div className='form-text'>Só contas analíticas do plano de contas deste período.</div>
                    </div>
                    {modo.tipo !== 'excecao' && !componente && (
                        <div className='col-12'>
                            <label className='form-label required'>Padrões de histórico do extrato</label>
                            <textarea className='form-control' rows={4} value={padroes}
                                placeholder={'Um por linha. Ex.:\nTAR PACOTE\nTARIFA BANCARIA'}
                                onChange={e => setPadroes(e.target.value)} />
                            <div className='form-text'>A linha do extrato entra na categoria quando o histórico contém o trecho (sem diferença de maiúsculas e acentos).</div>
                        </div>
                    )}
                    <div className='col-6'>
                        <label className='form-label'>Início da vigência</label>
                        <input type='date' className='form-control' value={vigencia}
                            onChange={e => setVigencia(e.target.value)} />
                        <div className='form-text'>
                            {versaoAnterior
                                ? 'Em branco: vale desde o início da versão anterior e a substitui.'
                                : 'Em branco: vale para qualquer data.'}
                        </div>
                    </div>
                    {versaoAnterior && (
                        <div className='col-6 d-flex align-items-end'>
                            <div className='text-muted fs-7'>Será gravada a versão {versaoAnterior.versao + 1}. A versão {versaoAnterior.versao} fica no histórico.</div>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className='btn btn-light' onClick={onHide} disabled={salvando}>Cancelar</button>
                <button className='btn btn-primary' onClick={salvar} disabled={salvando}>
                    {salvando && <span className='spinner-border spinner-border-sm me-2'></span>}
                    Salvar e aprovar
                </button>
            </Modal.Footer>
        </Modal>
    );
}
